import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import axiosInstance from 'utils/axiosUtil';
import { withRouter } from 'utils';
import { TitleTooltip, DynamicTable } from 'components';
import { getCmsAnnualLimit } from './starkConstants';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLOR = { ok: 'success', warning: 'warning', exceeded: 'error' };
const STATUS_LABEL = { ok: 'Okay', warning: 'Near Limit', exceeded: 'Over Limit' };

const currentYear  = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].filter((y) => y >= 2026);

function formatDateOnly(dateStr) {
  if (!dateStr) return '—';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length !== 3) return dateStr;
  const [yr, mo, dy] = parts;
  return `${mo}/${dy}/${yr}`;
}

function formatAmount(val) {
  const n = parseFloat(val);
  return isNaN(n) ? '$0.00' : `$${n.toFixed(2)}`;
}

function sortLogs(logs, order) {
  if (!order || !order.length) return logs;
  const entry = order[0];
  const [field, dir] = Array.isArray(entry) ? entry : [entry.slice(0, -1).join('.'), entry.slice(-1)[0]];
  const asc = String(dir).toUpperCase() !== 'DESC';
  return [...logs].sort((a, b) => {
    let av = a[field], bv = b[field];
    if (field === 'allocated_amount') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    else { av = String(av == null ? '' : av).toLowerCase(); bv = String(bv == null ? '' : bv).toLowerCase(); }
    if (av < bv) return asc ? -1 : 1;
    if (av > bv) return asc ?  1 : -1;
    return 0;
  });
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  spacer: { flex: 1 },
  progressBar: {
    height: 6,
    borderRadius: 3,
    width: 80,
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: '1px solid rgba(0,0,0,0.12)',
  },
});

// ── Component ──────────────────────────────────────────────────────────────────

class ProviderStarkTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      year:    currentYear,
      logs:    [],
      loading: false,
      query:   { limit: 25, offset: 0, order: [['spending_date', 'DESC']] },
    };
  }

  componentDidMount() {
    this._fetchLogs();
  }

  componentDidUpdate(prevProps, prevState) {
    const { clientId, provider } = this.props;
    const { year } = this.state;
    if (
      prevProps.clientId    !== clientId    ||
      prevProps.provider?.id !== provider?.id ||
      prevState.year        !== year
    ) {
      this._fetchLogs();
    }
  }

  _fetchLogs() {
    const { clientId, provider } = this.props;
    const { year } = this.state;
    if (!clientId || !provider?.id) return;

    this.setState({ loading: true });
    axiosInstance
      .get('/v2/stark/logs', { params: { clientId, year, filterProviderId: provider.id } })
      .then((res) => this.setState({ logs: res.data || [], loading: false }))
      .catch((err) => {
        console.error('ProviderStarkTab fetch error', err);
        this.setState({ logs: [], loading: false });
      });
  }

  exportCsv = () => {
    const { clientId, provider } = this.props;
    const { year } = this.state;
    const params = new URLSearchParams({
      clientId,
      year,
      filterProviderId: provider.id,
    });
    axiosInstance
      .get(`/v2/stark/export?${params.toString()}`, { responseType: 'blob' })
      .then((res) => {
        const url  = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href  = url;
        link.setAttribute(
          'download',
          `Stark_${provider.last_name || provider.id}_${year}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error('Stark CSV export failed', err));
  };

  render() {
    const { classes, provider, router } = this.props;
    const { year, logs, loading, query } = this.state;

    const providerId   = provider?.id;
    const annualLimit  = getCmsAnnualLimit(year);

    // Attach per-provider allocated_amount to each log for easy column access
    const enrichedLogs = (logs || []).map((log) => {
      const alloc = (log.allocations || []).find((a) => a.provider_id === providerId);
      return { ...log, allocated_amount: parseFloat(alloc?.allocated_amount || 0) };
    });

    const ytdTotal = enrichedLogs.reduce((sum, log) => sum + log.allocated_amount, 0);
    const pctUsed  = annualLimit > 0 ? Math.min(Math.round((ytdTotal / annualLimit) * 100), 100) : 0;
    const status   = ytdTotal >= annualLimit ? 'exceeded' : pctUsed >= 80 ? 'warning' : 'ok';

    // Client-side sort + paginate
    const sorted  = sortLogs(enrichedLogs, query.order);
    const paged   = sorted.slice(query.offset, query.offset + query.limit);
    const tableData = loading
      ? { placeholder: true, showLoading: true }
      : { count: enrichedLogs.length, rows: paged };

    const columns = [
      {
        key:    'spending_date',
        label:  'Date',
        style:  { width: 90 },
        format: (v) => formatDateOnly(v),
      },
      {
        key:      'category_label',
        label:    'Category',
        style:    { width: 130 },
        sortable: false,
        format:   (v) => v || '—',
      },
      {
        key:      'description',
        label:    'Description',
        sortable: false,
        format:   (v) => v || '—',
      },
      {
        key:    'allocated_amount',
        label:  'Allocated',
        style:  { width: 110 },
        format: (v) => formatAmount(v),
      },
      {
        key:    'user_name',
        label:  'Logged By',
        style:  { width: 130 },
        format: (v) => v || '—',
      },
    ];

    return (
      <Card>
        <CardContent>
          <div className={classes.cardHeader}>
            <TitleTooltip
              title='Non-Monetary Compensation'
              onExport={this.exportCsv}
            />
            <div className={classes.spacer} />
            <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
              {year} limit: <strong>{formatAmount(annualLimit)}</strong>
            </Typography>
            <Select
              value={year}
              onChange={(e) => this.setState({ year: Number(e.target.value) })}
              sx={{ minWidth: 90 }}
            >
              {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </div>

          <DynamicTable
            columns={columns}
            data={tableData}
            query={query}
            onQueryChange={(q) => this.setState({ query: q })}
            onRowClick={(row) => router.navigate(`/notes/stark/${row.id}`)}
            showFilters={false}
            noDataMessage={`No non-monetary compensation entries for ${year}.`}
            ContainerType={Fragment}
          />

          {/* YTD total footer */}
          {!loading && enrichedLogs.length > 0 && (
            <div className={classes.totalRow}>
              <Box display='flex' alignItems='center' gap={1}>
                <LinearProgress
                  variant='determinate'
                  value={pctUsed}
                  color={STATUS_COLOR[status]}
                  className={classes.progressBar}
                />
                <Typography variant='body2'>{pctUsed}%</Typography>
              </Box>
              <Chip
                label={STATUS_LABEL[status]}
                color={STATUS_COLOR[status]}
                variant='outlined'
                size='small'
                sx={{
                  width: 88,
                  borderWidth: 1,
                  '& .MuiChip-label': { color: 'text.primary', fontWeight: 300, textAlign: 'center', width: '100%' },
                }}
              />
              <Typography variant='body2' fontWeight={600}>
                {year} YTD: {formatAmount(ytdTotal)}
                <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                  of {formatAmount(annualLimit)}
                </Typography>
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
}

ProviderStarkTab.propTypes = {
  classes:  PropTypes.object.isRequired,
  provider: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  router:   PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(ProviderStarkTab));
